import {
  FORCES,
  type Force,
  type Rating,
} from "@/lib/tool-kit/business-templates/five-forces";
import { wrapText } from "@/lib/tool-kit/charts/text";

/**
 * The five-forces diagram's geometry: rivalry in the middle, new entrants above,
 * substitutes below, suppliers left and buyers right, with an arrow from each outer
 * force into rivalry. Pure numbers and wrapped text, so the layout is tested without a
 * browser and the SVG component only draws it.
 */

export const DIAGRAM_WIDTH = 840;
export const DIAGRAM_HEIGHT = 640;
export const BOX_WIDTH = 230;
export const BOX_HEIGHT = 124;

const LEFT = 20;
const CENTER = 305;
const RIGHT = 590;
const TOP = 70;
const MIDDLE = 270;
const BOTTOM = 470;

const POSITIONS: Record<Force, { x: number; y: number }> = {
  entrants: { x: CENTER, y: TOP },
  suppliers: { x: LEFT, y: MIDDLE },
  rivalry: { x: CENTER, y: MIDDLE },
  buyers: { x: RIGHT, y: MIDDLE },
  substitutes: { x: CENTER, y: BOTTOM },
};

/** Light-to-strong tints for ratings 0–5. Text is dark on every one of them. */
export const RATING_FILLS: Record<Rating, string> = {
  0: "#f2f2f2",
  1: "#dff0e6",
  2: "#eef5dc",
  3: "#fbf0c8",
  4: "#f9dcc0",
  5: "#f2bdb0",
};

export interface DiagramBox {
  force: Force;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  titleLines: string[];
  ratingLine: string;
  noteLines: string[];
}

export type DiagramPoint = readonly [number, number];

export interface DiagramLayout {
  boxes: DiagramBox[];
  arrows: DiagramPoint[][];
}

const TITLE_STYLE = { fontSize: 14, bold: true } as const;
const NOTE_STYLE = { fontSize: 11 } as const;
const INNER = BOX_WIDTH - 24;

export interface DiagramContent {
  names: Record<Force, string>;
  ratings: Record<Force, Rating>;
  ratingLines: Record<Force, string>;
  /** The first line of the writer's notes for each force, or "". */
  notes: Record<Force, string>;
}

export function layoutDiagram(content: DiagramContent): DiagramLayout {
  const boxes = FORCES.map((force): DiagramBox => {
    const { x, y } = POSITIONS[force];
    return {
      force,
      x,
      y,
      width: BOX_WIDTH,
      height: BOX_HEIGHT,
      fill: RATING_FILLS[content.ratings[force]],
      titleLines: wrapText(content.names[force], INNER, TITLE_STYLE, 2),
      ratingLine: content.ratingLines[force],
      noteLines: wrapText(content.notes[force], INNER, NOTE_STYLE, 3),
    };
  });
  const centreX = CENTER + BOX_WIDTH / 2;
  const centreY = MIDDLE + BOX_HEIGHT / 2;
  const arrows: DiagramPoint[][] = [
    [
      [centreX, TOP + BOX_HEIGHT],
      [centreX, MIDDLE],
    ],
    [
      [centreX, BOTTOM],
      [centreX, MIDDLE + BOX_HEIGHT],
    ],
    [
      [LEFT + BOX_WIDTH, centreY],
      [CENTER, centreY],
    ],
    [
      [RIGHT, centreY],
      [CENTER + BOX_WIDTH, centreY],
    ],
  ];
  return { boxes, arrows };
}
